<template>
	<div class="subscription-page">
		<!-- 查询区域 -->
		<el-card class="search-card" shadow="hover">
			<el-form :inline="true" :model="queryForm">
				<el-form-item label="订阅时间">
					<el-date-picker 
						v-model="queryForm.queryTime" 
						type="datetimerange" 
						range-separator="至" 
						start-placeholder="开始日期"
						end-placeholder="结束日期" 
						value-format="YYYY-MM-DD HH:mm:ss"
						:default-time="[new Date(2000, 1, 1, 0, 0, 0), new Date(2000, 1, 1, 23, 59, 59)]"
						style="width: 400px;"
					/>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" @click="handleQuery" :loading="loading">
						<el-icon style="margin-right: 5px;"><Search /></el-icon>
						查询
					</el-button>
					<el-button @click="resetQuery">重置</el-button>
				</el-form-item>
			</el-form>
		</el-card>

		<!-- 表格区域 -->
		<el-card class="table-card" shadow="never">
			<el-table 
				:data="subscriptionList" 
				style="width: 100%" 
				v-loading="loading"
				border
				stripe
				:header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
			>
				<el-table-column prop="waybillNo" label="运单号" width="220">
					<template #default="scope">
						<div class="waybill-cell">
							<span 
								class="waybill-no" 
								@click="openTrackDetail(scope.row)"
								title="点击查看轨迹"
							>
								{{ scope.row.waybillNo }}
							</span>
							<el-tooltip content="复制运单号" placement="top">
								<el-icon 
									class="copy-icon" 
									@click="copyWaybill(scope.row.waybillNo)"
								>
									<CopyDocument />
								</el-icon>
							</el-tooltip>
						</div>
					</template>
				</el-table-column>

				<el-table-column 
					prop="updateStatus" 
					label="最新状态" 
					width="120" 
					align="center"
					:filters="[
						{ text: '未发出', value: '' },
						{ text: '已取件', value: 'GOT' },
						{ text: '已揽收', value: 'COLLECT' },
						{ text: '运输中', value: 'TRANSPORT' },
						{ text: '派送中', value: 'DELIVERING' },
						{ text: '已签收', value: 'SUCCESS' },
						{ text: '异常/拒收', value: 'FAILED' }
					]" 
					:filter-method="filterHandler"
				>
					<template #default="scope">
						<el-tag :type="getStatusTagType(scope.row.updateStatus)" effect="light" round size="small">
							{{ scope.row.updateStatusName || '未知' }}
						</el-tag>
					</template>
				</el-table-column>

				<el-table-column prop="updateDescription" label="最新轨迹" min-width="250" show-overflow-tooltip />
				<el-table-column prop="uploadTime" label="轨迹更新时间" />
				<el-table-column prop="creationTime" label="订阅时间" width="180" sortable />
				
				<el-table-column label="操作" width="100" align="center" fixed="right">
					<template #default="scope">
						<el-button link type="primary" size="small" @click="openTrackDetail(scope.row)">
							查看轨迹
						</el-button>
					</template>
				</el-table-column>
			</el-table>
			
			<!-- 如果列表为空 -->
			<el-empty v-if="!loading && subscriptionList.length === 0" description="暂无订阅数据" />
		</el-card>
	</div>
</template>

<script>
	import { Search, CopyDocument } from '@element-plus/icons-vue';
	import dayjs from 'dayjs';

	export default {
		components: { Search, CopyDocument },
		data() {
			return {
				loading: false,
				queryForm: {
					queryTime: []
				},
				subscriptionList: []
			}
		},
		created() {
			this.initDefaultTime();
			this.handleQuery(); // 初始加载一次数据
		},
		methods: {
			// 状态标签颜色映射
			getStatusTagType(status) {
				const map = {
					'': 'info',
					'GOT': 'info',
					'COLLECT': '',
					'TRANSPORT': 'primary',
					'DELIVERING': 'warning',
					'SUCCESS': 'success',
					'FAILED': 'danger',
					'REJECT': 'danger'
				};
				return map[status] || 'info';
			},
			
			// 表格过滤逻辑
			filterHandler(value, row) {
				// 注意：这里应该对比原始数据字段 updateStatus，而不是显示的名称
				return row.updateStatus === value;
			},

			// 查询
			handleQuery() {
				this.loading = true;
				// 构造请求参数，如果时间为空则不传或传默认
				const params = { ...this.queryForm };
				
				this.apiService.post({
						modules: "getSubscriptionList",
						content: params
					})
					.then((res) => {
						if (res.status) {
							this.subscriptionList = res.data.subscriptionList || [];
						} else {
							this.$message.error(res.msg || '查询失败');
							this.subscriptionList = [];
						}
					})
					.catch(err => {
						console.error(err);
						this.$message.error('网络异常');
					})
					.finally(() => {
						this.loading = false;
					});
			},

			// 重置
			resetQuery() {
				this.initDefaultTime();
				this.handleQuery();
			},

			// 初始化默认时间（最近1天）
			initDefaultTime() {
				try {
					const endDate = dayjs().endOf('day');
					const startDate = dayjs().subtract(1, 'day').startOf('day');
					this.queryForm.queryTime = [
						startDate.format('YYYY-MM-DD HH:mm:ss'),
						endDate.format('YYYY-MM-DD HH:mm:ss')
					];
				} catch (e) {
					console.error("时间初始化失败", e);
					this.queryForm.queryTime = [];
				}
			},

			// 打开轨迹详情弹窗
			openTrackDetail(row) {
				if (!row.waybillNo) return;
				// 假设父组件或全局有一个 waybillQueryPopup 方法
				if (typeof this.waybillQueryPopup === 'function') {
					this.waybillQueryPopup({
						cpCode: row.cpCode,
						waybillNo: row.waybillNo,
						phone: row.phone || ''
					});
				} else {
					// 如果没有全局方法，可以这里 emit 事件或者路由跳转
					console.log('Open track for:', row.waybillNo);
					this.$message.info(`查看运单: ${row.waybillNo}`);
				}
			},

			// 复制运单号
			copyWaybill(text) {
				if (!text) return;
				// 使用 navigator.clipboard 或兼容方案
				if (navigator.clipboard && navigator.clipboard.writeText) {
					navigator.clipboard.writeText(text).then(() => {
						this.$message.success('复制成功');
					}).catch(() => {
						this.fallbackCopy(text);
					});
				} else {
					this.fallbackCopy(text);
				}
			},

			fallbackCopy(text) {
				const input = document.createElement('input');
				input.value = text;
				document.body.appendChild(input);
				input.select();
				document.execCommand('copy');
				document.body.removeChild(input);
				this.$message.success('复制成功');
			}
		}
	}
</script>

<style scoped lang="scss">
	.subscription-page {
		padding: 20px;
		background-color: #f5f7fa;
		min-height: 100vh;
	}

	.search-card {
		margin-bottom: 20px;
	}

	.table-card {
		min-height: 500px;
	}

	.waybill-cell {
		display: flex;
		align-items: center;
		gap: 8px;

		.waybill-no {
			color: #409eff;
			cursor: pointer;
			font-weight: 500;
			
			/* &:hover {
				text-decoration: underline;
			} */
		}

		.copy-icon {
			cursor: pointer;
			color: #909399;
			transition: color 0.3s;
			
			&:hover {
				color: #409eff;
			}
		}
	}
</style>